// Admin (managers: the Admin feature, `backend/src/access.ts`). Five tabs (Import only while
// `ROUTING_IMPORT` is on): Customer assignments
// (`customer-assignments.tsx`: who looks after which customers), Customers (`customer-names-tab.tsx`:
// the name every page shows for a customer), Routing rules (`routing-rules-tab.tsx`: each customer's
// routing window), Import (`import-tab.tsx`: the PRODUCTION CHECK OFF workbook onto the lines) and
// Divisions: every
// division the app's data has — A2000's and Deposco's business units (EGC, JA, WM), the Source filter
// picks — with its open lines and the abbreviation every page shows for it,
// edited in place. None saved is the default, the first 7 characters of the name — a division that
// appears later has it at once; Reset to default goes back to it. Each save is a change of its own
// in the System log (restorable); the server refuses anyone but the Admin feature.

import { Fragment, useMemo, useRef, useState } from "react";
import { RotateCcwIcon } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ABBREV_MAX,
  DEFAULT_ABBREV_CHARS,
  DIVISION_ABBREVS,
  ROUTING_API,
  adminTabIds,
  adminTabOf,
  adminTabSearch,
  checkAbbrev,
  countText,
  inSource,
  divisionText,
  formatDay,
} from "@app/shared";
import { ADMIN_TAB_NAMES, adminViewKey, pushStep, undoableStep } from "@app/shared";
import type {
  AdminTabId,
  AdminView,
  AssignmentsPayload,
  CustomerNamesPayload,
  DivisionRow,
  DivisionsPayload,
  RoutingRulesPayload,
  RuleKind,
  SourceFilter,
  ViewStep,
} from "@app/shared";
import {
  Button,
  DataError,
  Input,
  RegisteredPage,
  TableSkeleton,
  apiGet,
  crudApi,
  crudErrorText,
  useMe,
} from "@axny/platform-ui";
import { cn } from "@/lib/utils";
import { useSourceVersion } from "./data";
import { useLoggedWrites } from "./logged-write";
import { MessageProvider, notify, useMessage } from "./message";
import { SourceBadge, SourcePicker, useSourceFilter } from "./source-filter";
import { SearchBox } from "./toolbar";
import { CustomerAssignmentsTab } from "./customer-assignments";
import { CustomerNamesTab } from "./customer-names-tab";
import { RoutingRulesTab } from "./routing-rules-tab";
import { ImportTab, importLeaveGuard } from "./import-tab";
import { AskAiBar } from "./ask-ai-bar";
import { AdminAskContext, useAdminAskAi, useAdminAskTab } from "./axai-admin";
import type { AdminAskSlot, AdminAskTab, AdminReads } from "./axai-admin";
import type { AdminBarTab } from "./ask-ai-bar";
import { ROUTING_RULES_KEY } from "./routing-rules-tab";
import { CUSTOMER_NAMES_KEY } from "./customer-names-tab";
import { ASSIGNMENTS_KEY } from "./customer-assignments";

export const DIVISIONS_KEY = ["routing", "admin", "divisions"] as const;

/**
 * A link may open a tab (`?admin=`), and Routing rules on an In use filter (`?rules=none`). Import is
 * offered only while the server's `ROUTING_IMPORT` is on (`/version`'s `importOn`); off, or the answer
 * unreadable, it is not, and `?admin=import` opens the first tab.
 */
export function AdminPage({ tab, rules }: { tab?: AdminTab; rules?: RuleKind }) {
  const version = useSourceVersion();
  const importOn = version.data?.importOn === true;
  return (
    <RegisteredPage id="admin">
      <MessageProvider>
        {version.isPending ? (
          <TableSkeleton />
        ) : (
          <AdminScreen
            key={`${tab ?? ""}|${rules ?? ""}|${importOn}`}
            initialTab={adminTabOf(tab, importOn)}
            tabs={adminTabIds(importOn)}
            rules={rules}
          />
        )}
      </MessageProvider>
    </RegisteredPage>
  );
}

/** Each tab with its own sentence, shown under the tabs (the page's description says only what Admin is). */
const ADMIN_TABS = [
  {
    id: "divisions",
    label: "Divisions",
    about:
      "The abbreviation every page shows for an A2000 division or a Deposco business unit, its full name on hover — by default the first 7 characters of the name.",
  },
  {
    id: "customers",
    label: "Customers",
    about:
      "The name every page shows for an A2000 customer or a Deposco trading partner (several may share one), the original on hover — by default A2000 its code, Deposco its name.",
  },
  {
    id: "routing-rules",
    label: "Routing rules",
    about:
      "How many business days before First ship date a customer's lines open for routing, and before Cancel date they must be routed. Set it here to override A2000 (CUST_REF1 / CUST_REF2). Without a setting here, A2000's is used. Nothing is written back to A2000.",
  },
  {
    id: "assignments",
    label: "Customer assignments",
    about:
      "Who looks after which customers (Owner, Backup), the codes each label covers, and the customers with open lines and no owner.",
  },
  {
    id: "import",
    label: "Import",
    about:
      "Load what people typed on their open work in the PRODUCTION CHECK OFF workbook onto the app's lines: upload the file, review what it found, decide the open points, then run it. Customer, dates, qty, PO / PT and WMS always come from A2000 and Deposco, never from the file.",
  },
] as const satisfies readonly { id: AdminTabId; label: string; about: string }[];
export type AdminTab = AdminTabId;

function AdminScreen({ initialTab, tabs, rules }: { initialTab: AdminTab; tabs: AdminTab[]; rules?: RuleKind }) {
  const [tab, setTab] = useState<AdminTab>(initialTab);
  const queryClient = useQueryClient();
  // Ask AI (`axai-admin.ts`): the open tab registers its filters in `slot`; Undo goes back a step.
  const [tabView, setTabView] = useState<AdminView | null>(null);
  // A tab Ask AI opened (a question about another tab's filters, or Undo back to one): resolved when
  // that tab has registered its filters.
  const opening = useRef<{ tab: AdminTab; done: (t: AdminAskTab) => void } | null>(null);
  const slot = useMemo<AdminAskSlot>(() => {
    const s: AdminAskSlot = {
      tab: { current: null },
      changed: (view) => {
        setTabView(view);
        const o = opening.current;
        if (o && view.tab === o.tab && s.tab.current) {
          opening.current = null;
          o.done(s.tab.current);
        }
      },
    };
    return s;
  }, []);
  // The tabs a question's filters are on when no one tab has them all: links under the bar.
  const [offer, setOffer] = useState<AdminBarTab[] | null>(null);
  // Every way to a tab — a click, Ask AI's open, its Undo — comes here, and the address follows it
  // (`adminTabSearch`): replaced, no new history entry, so a refresh or a copied link opens this tab.
  // Not through the router: its new search would remount the page (`AdminPage`'s key) and lose Undo.
  const switchTo = (t: AdminTab) => {
    setOffer(null);
    setTab(t);
    const search = adminTabSearch(window.location.search, t);
    if (search !== window.location.search)
      window.history.replaceState(
        window.history.state,
        "",
        `${window.location.pathname}${search}${window.location.hash}`,
      );
  };
  const open = (to: AdminBarTab) =>
    new Promise<AdminAskTab>((resolve, reject) => {
      const now = slot.tab.current;
      if (now?.view.tab === to) return resolve(now);
      opening.current = { tab: to, done: resolve };
      switchTo(to);
      setTimeout(() => {
        if (opening.current?.tab !== to) return;
        opening.current = null;
        reject(new Error(`${ADMIN_TAB_NAMES[to]} did not open`));
      }, 10_000);
    });
  const [aiSteps, setAiSteps] = useState<ViewStep<AdminView>[]>([]);
  const turnBefore = useRef<AdminView | null>(null);
  const version = tabView ? adminViewKey(tabView) : tab;
  const aiStep = undoableStep(aiSteps, version);
  // The tabs' own reads, by their own keys: what a tab already read is not read again.
  const reads = useMemo<AdminReads>(() => {
    const read =
      <T,>(key: readonly unknown[], path: string) =>
      () =>
        queryClient.fetchQuery({
          queryKey: key,
          queryFn: ({ signal }) => apiGet<T>(`${ROUTING_API}/admin/${path}`, signal),
          staleTime: 10_000,
        });
    return {
      rules: read<RoutingRulesPayload>(ROUTING_RULES_KEY, "routing-rules"),
      customers: read<CustomerNamesPayload>(CUSTOMER_NAMES_KEY, "customers"),
      assignments: read<AssignmentsPayload>(ASSIGNMENTS_KEY, "assignments"),
      divisions: read<DivisionsPayload>(DIVISIONS_KEY, "divisions"),
    };
  }, [queryClient]);
  const barTab = tab === "import" ? null : tab;
  useAdminAskAi({
    tab: barTab ?? "divisions",
    slot,
    reads,
    open,
    offer: setOffer,
    apply: (next, unapplied, from) => {
      const before = from ?? slot.tab.current?.view;
      if (!before) return;
      const question = turnBefore.current;
      setAiSteps((steps) =>
        pushStep(steps, { before: question ?? before, version: adminViewKey(next), unapplied }, question),
      );
      slot.tab.current?.apply(next);
    },
  });
  return (
    <AdminAskContext.Provider value={slot}>
      <div className="routing-page space-y-3">
        {/* Kept mounted across tabs: a question that opens another tab goes on there. */}
        {barTab ? (
          <AskAiBar
            mode={`admin-${barTab}`}
            version={version}
            step={aiStep}
            onBegin={() => {
              setOffer(null);
              turnBefore.current = slot.tab.current?.view ?? null;
            }}
            onEnd={() => {
              turnBefore.current = null;
            }}
            onUndo={() => {
              if (!aiStep) return;
              // Back to the tab and the view the step left (another tab, when Ask AI opened this one).
              const before = aiStep.before;
              setAiSteps((steps) => steps.slice(0, -1));
              void open(before.tab).then((t) => t.apply(before));
            }}
          />
        ) : null}
        {barTab && offer?.length ? (
          <p className="text-muted-foreground -mt-1 text-xs" data-testid="admin-ask-open">
            Open{" "}
            {offer.map((t, i) => (
              <Fragment key={t}>
                {i ? " or " : ""}
                <button type="button" className="text-primary font-medium hover:underline" onClick={() => switchTo(t)}>
                  {ADMIN_TAB_NAMES[t]}
                </button>
              </Fragment>
            ))}{" "}
            for the rest of that question.
          </p>
        ) : null}
        <div className="flex gap-1 border-b" role="tablist">
          {ADMIN_TABS.filter((t) => tabs.includes(t.id)).map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={cn(
                "-mb-px border-b-2 px-3 py-2 text-sm font-medium",
                tab === t.id ? "border-primary" : "text-muted-foreground hover:text-foreground border-transparent",
              )}
              // A run in progress, or a result not downloaded, asks before the Import tab is left.
              onClick={() =>
                void (async () => {
                  if (
                    t.id !== tab &&
                    tab === "import" &&
                    importLeaveGuard.current &&
                    !(await importLeaveGuard.current())
                  )
                    return;
                  switchTo(t.id);
                })()
              }
              data-testid={`admin-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <p className="text-muted-foreground text-sm" data-testid="admin-about">
          {ADMIN_TABS.find((t) => t.id === tab)?.about}
        </p>
        {tab === "divisions" ? (
          <DivisionsTab />
        ) : tab === "customers" ? (
          <CustomerNamesTab />
        ) : tab === "routing-rules" ? (
          <RoutingRulesTab initialInUse={rules} />
        ) : tab === "import" ? (
          <ImportTab />
        ) : (
          <CustomerAssignmentsTab />
        )}
      </div>
    </AdminAskContext.Provider>
  );
}

function DivisionsTab() {
  const [search, setSearch] = useState("");
  const [source, setSource] = useSourceFilter();
  const data = useQuery({
    queryKey: DIVISIONS_KEY,
    queryFn: ({ signal }) => apiGet<DivisionsPayload>(`${ROUTING_API}/admin/divisions`, signal),
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
  const rows = data.data?.divisions ?? [];
  const keep = (search: string, source: SourceFilter) => {
    const q = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        inSource(r.source, source) &&
        (!q || [r.corp, r.div, r.name, r.abbrev, r.defaultAbbrev].join(" ").toLowerCase().includes(q)),
    );
  };
  const shown = keep(search, source);
  // Ask AI sets the search box and the Source (`axai-admin.ts`).
  useAdminAskTab({
    view: { tab: "divisions", search, source },
    apply: (v) => {
      if (v.tab !== "divisions") return;
      setSearch(v.search);
      setSource(v.source);
    },
    shownFor: (v) => (v.tab === "divisions" ? keep(v.search, v.source).length : 0),
  });

  if (data.isPending) return <TableSkeleton />;
  if (data.isError) return <DataError error={data.error} />;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <SourcePicker
          value={source}
          counts={{
            A2000: rows.filter((r) => r.source === "A2000").length,
            Deposco: rows.filter((r) => r.source === "Deposco").length,
          }}
          onChange={setSource}
        />
        <SearchBox value={search} onChange={setSearch} placeholder="Search divisions — name, div, abbreviation" />
        <p className="text-muted-foreground ml-auto text-xs">
          The abbreviation shows everywhere a division does, its full name on hover · by default the first 7 characters
          · at most {ABBREV_MAX} · on the next refresh of each page
        </p>
      </div>
      <div className="overflow-x-auto rounded-md border" data-testid="admin-divisions">
        <table className="w-full min-w-[820px] text-[13px]">
          <thead className="bg-locked text-left text-xs">
            <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:font-medium">
              <th className="w-24">Source</th>
              <th className="w-20">CO.</th>
              <th className="w-16">Div</th>
              <th>Division</th>
              <th className="w-24 text-right">Open lines</th>
              <th className="w-44">Abbreviation</th>
              <th className="w-56" />
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-muted-foreground p-6">
                  {rows.length ? "No division matches the search." : "No division in the data yet."}
                </td>
              </tr>
            ) : null}
            {shown.map((r) => (
              <DivisionLine key={r.key} row={r} rows={rows} />
            ))}
          </tbody>
        </table>
      </div>
      <footer className="text-muted-foreground flex flex-wrap gap-x-4 text-xs">
        <span>
          {countText(rows.length, "division")} in the open pick tickets and Deposco orders, pre-routed orders and
          History · {countText(rows.filter((r) => !r.isDefault).length, "abbreviation")} of their own
        </span>
        <span className="ml-auto">Display only: A2000 and Deposco data and saved records keep the full name</span>
      </footer>
    </div>
  );
}

/** One division: its abbreviation typed in place (Enter or leaving the cell saves; Esc puts it back). */
function DivisionLine({ row, rows }: { row: DivisionRow; rows: readonly DivisionRow[] }) {
  const message = useMessage();
  const writes = useLoggedWrites();
  const queryClient = useQueryClient();
  const me = useMe();
  const who = me.data?.authenticated ? me.data.user.displayName || me.data.user.username : "";
  const [typed, setTyped] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The default is the box's placeholder, never its text: typing replaces it, never adds to it.
  const value = typed ?? (row.isDefault ? "" : row.abbrev);

  /** Saves `abbreviation` ("" is the default), with its System log entry; every page reads it again. */
  const save = async (abbreviation: string, note: string) => {
    setBusy(true);
    const log = {
      action: "edit" as const,
      lineText: divisionText(row),
      changes: [{ f: "abbreviation", from: row.record?.abbreviation ?? "", to: abbreviation }],
      note,
    };
    try {
      const input = { corp: row.corp, div: row.div, division: row.name, abbreviation, updatedBy: who };
      if (row.record) {
        // Its version now: another manager may have saved it since.
        const now = await crudApi.get(DIVISION_ABBREVS.id, row.record.id);
        await writes.update(DIVISION_ABBREVS, row.record.id, String(now.rv), input, log);
      } else await writes.create(DIVISION_ABBREVS, input, log);
      setTyped(null);
      notify(
        abbreviation ? `${row.name}: ${abbreviation}` : `${row.name}: back to the default`,
        "Every page shows it on its next refresh.",
      );
    } catch (e) {
      await message.error("Couldn't save the abbreviation", crudErrorText(e), [
        "Someone may have saved it at the same time: the list is read again.",
      ]);
      setTyped(null);
    } finally {
      setBusy(false);
      await queryClient.invalidateQueries({ queryKey: ["routing"] });
    }
  };

  const commit = async () => {
    if (typed === null || typed.trim() === row.abbrev || (!typed.trim() && row.isDefault)) return setTyped(null);
    const check = checkAbbrev(typed, row.key, rows);
    if (!check.ok) {
      await message.error("Check the abbreviation", check.message);
      return;
    }
    if (check.value === row.abbrev) return setTyped(null);
    if (check.warning) {
      const ok = await message.confirm(check.warning.title, check.warning.text, {
        lines: check.warning.lines,
        confirmLabel: "Use it anyway",
      });
      if (!ok) return;
    }
    await save(check.value, check.value === row.defaultAbbrev ? "The same as the default" : "");
  };

  const saved = row.record && !row.isDefault ? row.record : null;
  return (
    <tr className={cn("border-t align-middle [&>td]:px-3 [&>td]:py-1.5", busy && "opacity-60")}>
      <td>
        <SourceBadge source={row.source} />
      </td>
      <td className="font-mono">{row.corp}</td>
      <td className="font-mono">{row.div}</td>
      <td>
        <p className="font-medium">{row.name}</p>
        {!row.seenIn.includes("open") ? (
          <p className="text-muted-foreground text-xs">
            {row.source === "Deposco"
              ? "No open line yet"
              : `${row.seenIn.includes("orders") ? "Pre-routed orders" : "History"} only`}
          </p>
        ) : null}
      </td>
      <td className="text-right tabular-nums">{row.openLines.toLocaleString("en-US")}</td>
      <td>
        <Input
          className="h-7 w-36 font-mono text-[13px]"
          aria-label={`Abbreviation of ${row.name}`}
          placeholder={row.defaultAbbrev}
          maxLength={ABBREV_MAX + 8}
          disabled={busy}
          value={value}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => {
            // Enter saves by leaving the cell: one save, never two. Its default is cancelled: the
            // same key would otherwise press the button of a dialog the save opens.
            if (e.key === "Enter") {
              e.preventDefault();
              e.currentTarget.blur();
            }
            if (e.key === "Escape") setTyped(null);
          }}
          onBlur={() => void commit()}
        />
      </td>
      <td>
        <div className="flex items-center gap-2">
          {saved ? (
            <span className="text-muted-foreground text-xs">
              {saved.updatedBy ? `${saved.updatedBy}` : "Saved"}
              {saved.updated_at ? ` · ${formatDay(saved.updated_at.slice(0, 10))}` : ""}
            </span>
          ) : (
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600">Default</span>
          )}
          {saved ? (
            <Button
              size="xs"
              variant="outline"
              className="ml-auto gap-1"
              disabled={busy}
              title={`Back to ${row.defaultAbbrev}, the first ${DEFAULT_ABBREV_CHARS} characters of the name`}
              onClick={() => void save("", `Reset to default (${row.defaultAbbrev})`)}
            >
              <RotateCcwIcon className="size-3.5" />
              Reset to default
            </Button>
          ) : null}
        </div>
      </td>
    </tr>
  );
}
